import React from 'react';
import { useTranslation } from 'react-i18next';
import { TileModal } from '@/components/Dashboard/TileModal';
import { useEvaluationModal } from '@/components/EvaluationModal/useEvaluationModal';
import { UnreadDot } from '@/components/Dashboard/UnreadDot';
import { useEvaluationsSummary } from '@/components/Dashboard/hooks/useEvaluationsSummary';
import { getEvaluationLabel } from '@/components/Dashboard/evaluationLabel';
import { getLessonTypeChipClass } from '@/components/Dashboard/lessonTypeChipClass';
import { LessonTypeChip } from '@/components/LessonTypeChip';
import {
  CommentChip,
  RatingChip,
  ScaleChip,
} from '@/components/Icons/MarkChipIcons';
import {
  formatMonth,
  formatShortDate,
  groupByMonth,
  sortByDateDesc,
} from '@/utils/date';
import { isNumeric } from '@/utils/text';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const GradesModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { data } = useEvaluationsSummary();
  const { onOpen: onOpenEvaluation } = useEvaluationModal();
  const sorted = sortByDateDesc(
    data?.evaluations ?? [],
    ({ timeCreated }) => timeCreated,
  );
  const groups = Array.from(
    groupByMonth(sorted, ({ timeCreated }) => timeCreated).values(),
  );

  return (
    <TileModal
      isOpen={isOpen}
      title={t('home.dashboard.grades.title')}
      onClose={onClose}
    >
      <div className="flex flex-col gap-6 px-4 py-4 md:px-6 md:py-6">
        {groups.map((items, monthIndex) => (
          <section key={monthIndex} className="flex flex-col gap-3">
            <h3 className="text-headline-20 capitalize text-foreground">
              {formatMonth(items[0].timeCreated)}
            </h3>
            <ul className="bg-card shadow-card flex flex-col rounded-xl">
              {items.map((evaluation) => {
                const chipClass = getLessonTypeChipClass(
                  evaluation.lesson.type.lessonTypeId,
                );
                const evaluationLabel = getEvaluationLabel(evaluation);

                return (
                  <li
                    key={evaluation.id}
                    className="flex cursor-pointer flex-col gap-1 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-hover md:min-h-[78px] md:flex-row md:items-center md:gap-4 md:px-5 md:py-4"
                    onClick={() => onOpenEvaluation(evaluation.id)}
                  >
                    <div className="flex items-center justify-between gap-3 md:hidden">
                      <span className="text-subhead-14 truncate text-foreground">
                        {evaluation.disciplineName}
                      </span>
                      <span className="text-body-13 md:text-body-14 flex shrink-0 items-center gap-1 text-subtle-foreground">
                        {formatShortDate(evaluation.lesson.date)}
                        {evaluation.isNew && <UnreadDot />}
                      </span>
                    </div>

                    <div className="hidden w-1/3 min-w-0 flex-col gap-1 md:flex">
                      <div className="flex items-center gap-1">
                        {evaluation.isNew && <UnreadDot />}
                        <span className="text-body-13 md:text-body-14 text-muted-foreground">
                          {formatShortDate(evaluation.lesson.date)}
                        </span>
                        <LessonTypeChip
                          name={evaluation.lesson.type.name}
                          lessonTypeId={evaluation.lesson.type.lessonTypeId}
                        />
                      </div>
                      <span className="text-subhead-14 truncate text-foreground">
                        {evaluation.disciplineName}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3 md:hidden">
                      <LessonTypeChip
                        name={evaluation.lesson.type.name}
                        lessonTypeId={evaluation.lesson.type.lessonTypeId}
                      />
                      <div className="flex shrink-0 items-center gap-1">
                        {evaluation.value && (
                          <span
                            className={`text-body-13 font-medium flex h-6 min-w-6 items-center justify-center rounded-xl px-2 tracking-[0.025em] text-foreground ${chipClass}`}
                          >
                            {evaluationLabel}
                          </span>
                        )}
                        {evaluation.isWeighted && <ScaleChip />}
                        {evaluation.lesson.isTest && <RatingChip />}
                      </div>
                    </div>

                    {evaluation.lesson.subject && (
                      <div
                        className="rich-text text-body-14 hidden flex-1 text-muted-foreground md:block"
                        dangerouslySetInnerHTML={{
                          __html: evaluation.lesson.subject,
                        }}
                      />
                    )}
                    {!evaluation.lesson.subject && (
                      <p className="text-body-14 hidden flex-1 text-muted-foreground md:block">
                        {t('home.dashboard.grades.noDescription')}
                      </p>
                    )}

                    <div className="hidden shrink-0 flex-col items-end gap-2 md:flex">
                      {evaluation.value && (
                        <span
                          className={`text-body-13 flex h-7 items-center justify-center font-medium tracking-[0.025em] text-foreground ${isNumeric(evaluationLabel) ? 'w-7' : 'min-w-7 px-2'} rounded-full ${chipClass}`}
                        >
                          {evaluationLabel}
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        {evaluation.hasComments && <CommentChip />}
                        {evaluation.isWeighted && <ScaleChip />}
                        {evaluation.lesson.isTest && <RatingChip />}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </TileModal>
  );
};
